import { useEffect, useState } from 'react'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'

const getFilePageCount = async (file: File): Promise<number> => {
  if (!file.type.includes('pdf')) {
    return 1
  }

  try {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.readAsArrayBuffer(file)
      reader.onloadend = function () {
        if (!reader.result) {
          resolve(1)
          return
        }

        const buffer = new Uint8Array(reader.result as ArrayBuffer)
        const data = Array.from(buffer)
          .map((b) => String.fromCharCode(b))
          .join('')

        // Match PDF page markers
        const pageMatches = data.match(/\/Type[\s]*\/Page[^s]/g)
        const count = pageMatches?.length || 1
        resolve(count)
      }

      reader.onerror = function () {
        console.error('Error reading file:', reader.error)
        resolve(1)
      }
    })
  } catch (error) {
    console.error('Error getting PDF page count:', error)
    return 1
  }
}

export const getTotalPageCount = async (files: File[]): Promise<number> => {
  if (!files.length) return 0

  const pageCounts = await Promise.all(
    files.map((file) => getFilePageCount(file)),
  )

  return pageCounts.reduce((total, count) => total + count, 0)
}

interface FaxCoverPageProps {
  coverPageNote: string
  pageCount: number
  toNumber: string
  fromNumber: string
}

const createCoverPageFile = async ({
  coverPageNote,
  pageCount,
  toNumber,
  fromNumber,
}: FaxCoverPageProps) => {
  try {
    // Create a canvas directly
    const canvas = document.createElement('canvas')
    const width = 816 // 8.5 inches at 96 DPI
    const height = 1056 // 11 inches at 96 DPI
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }

    // Draw white background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, width, height)

    // Set up text properties
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'top'

    // Header section
    const padding = 40
    let y = padding

    // Draw title
    ctx.font = 'bold 24px Helvetica'
    ctx.fillText('FAX COVER PAGE', padding, y)

    // Draw date
    ctx.font = '14px Helvetica'
    const date = new Date().toLocaleDateString()
    const dateText = `Date: ${date}`
    const dateWidth = ctx.measureText(dateText).width
    ctx.fillText(dateText, width - padding - dateWidth, y)

    // Draw horizontal line
    y += 30
    ctx.beginPath()
    ctx.moveTo(padding, y)
    ctx.lineTo(width - padding, y)
    ctx.strokeStyle = 'black'
    ctx.lineWidth = 1
    ctx.stroke()

    // Content section
    y += 20
    ctx.font = 'bold 12px Helvetica'

    // TO section
    ctx.fillText('TO:', padding, y)
    y += 20
    ctx.font = '12px Helvetica'
    ctx.fillText(toNumber, padding, y)

    // FROM section
    y += 30
    ctx.font = 'bold 12px Helvetica'
    ctx.fillText('FROM:', padding, y)
    y += 20
    ctx.font = '12px Helvetica'
    ctx.fillText(fromNumber, padding, y)

    // PAGES section
    y += 30
    ctx.font = 'bold 12px Helvetica'
    ctx.fillText('PAGES:', padding, y)
    y += 20
    ctx.font = '12px Helvetica'
    const pagesText = `${pageCount ? pageCount + 1 : 1} (including cover page)`
    ctx.fillText(pagesText, padding, y)

    // Notes section if provided
    if (coverPageNote) {
      y += 40

      // Draw border
      ctx.strokeStyle = 'black'
      ctx.lineWidth = 1
      ctx.strokeRect(padding, y, width - padding * 2, 150)

      // Draw notes header
      y += 15
      ctx.font = 'bold 12px Helvetica'
      ctx.fillText('NOTES:', padding + 15, y)

      // Draw notes content - with word wrapping
      y += 25
      ctx.font = '12px Helvetica'
      const maxWidth = width - padding * 2 - 30
      const words = coverPageNote.split(' ')
      let line = ''

      for (const word of words) {
        const testLine = line + word + ' '
        const metrics = ctx.measureText(testLine)
        const testWidth = metrics.width

        if (testWidth > maxWidth) {
          ctx.fillText(line, padding + 15, y)
          line = word + ' '
          y += 20
        } else {
          line = testLine
        }
      }

      // Draw last line
      if (line) {
        ctx.fillText(line, padding + 15, y)
      }
    }

    // Get image data from canvas
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((result) => {
        if (result) resolve(result)
        else resolve(new Blob([], { type: 'image/png' }))
      }, 'image/png')
    })

    return new File([blob], 'Cover Page.png', { type: 'image/png' })
  } catch (error) {
    console.error('Error creating cover page image:', error)
    throw error
  }
}

export const useFaxCoverPage = ({
  toNumber,
  fromNumber,
}: Omit<FaxCoverPageProps, 'note' | 'pageCount' | 'coverPageNote'>) => {
  const [coverPageNote, setCoverPageNote] = useState('')
  const [pageCount, setPageCount] = useState(0)
  const [coverPageFile, setCoverPageFile] = useState<File | null>(null)

  const {
    faxModal: [faxModalState],
  } = useModalState()
  const {
    settings: {
      fax: { includeCoverPage },
    },
  } = useSettingsState()

  const [_includeCoverPage, setIncludeCoverPage] = useState(includeCoverPage)

  useEffect(() => {
    setIncludeCoverPage(includeCoverPage)
  }, [includeCoverPage, faxModalState.open])

  const _handleSetCoverPageFile = async () => {
    const coverPageFile = await createCoverPageFile({
      coverPageNote,
      pageCount,
      toNumber,
      fromNumber,
    })
    setCoverPageFile(coverPageFile)
  }

  useEffect(() => {
    if (_includeCoverPage) {
      _handleSetCoverPageFile()
    }

    if (!_includeCoverPage) {
      setCoverPageFile(null)
    }
  }, [
    _includeCoverPage,
    setIncludeCoverPage,
    fromNumber,
    toNumber,
    coverPageNote,
    pageCount,
  ])

  const handleSetCoverPageCount = (files: File[]) => {
    getTotalPageCount(files).then((count) => {
      setPageCount(count)
    })
  }

  return {
    includeCoverPage: _includeCoverPage,
    setIncludeCoverPage,
    coverPageFile,
    coverPageNote,
    handleSetCoverPageCount,
    setCoverPageNote,
  }
}
